import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { ActionCardComponent } from '../../../shared/components/action-card/action-card.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ConfigEstoqueService } from '../../../shared/services/apis/suprimentos/config-estoque.service';
import { LocalizacaoEstoqueService } from '../../../shared/services/apis/produto/localizacao-estoque.service';
import { InventarioOrquestradorService } from '../../../shared/services/apis/orquestrador-suprimentos/inventario-orquestrador.service';
import {
  InventarioImportacaoService,
  ValidarPlanilhaRequest,
} from '../../../shared/services/apis/orquestrador-suprimentos/inventario-importacao.service';
import { LocalizacaoEstoqueStruct } from '../../../shared/services/responses/produto/get-localizacao-estoque.response';
import {
  ImportarPlanilhaPreviewDialogComponent,
  ImportarPreviewResult,
} from './dialogs/importar-planilha-preview-dialog/importar-planilha-preview-dialog.component';

@Component({
  selector: 'app-inventario-importar-planilha',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatRadioModule,
    RouterModule,
    FooterActionComponent,
    AlertCardComponent,
    ActionCardComponent,
  ],
  templateUrl: './inventario-importar-planilha.component.html',
  styleUrl: './inventario-importar-planilha.component.css',
})
export class InventarioImportarPlanilhaComponent implements OnInit {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_InventarioEstoque;

  public uploadForm: FormGroup;

  public _isValidating = false;
  public _isProcessing = false;
  public _isDragging = false;
  public _isLoadingLocais = false;
  public _isDownloadingModelo = false;

  public _arquivo: File | null = null;
  public _localizacoes: LocalizacaoEstoqueStruct[] = [];
  public _isEditarQuantidadeHabilitado = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private alertService: AlertService,
    private localizacaoService: LocalizacaoEstoqueService,
    private configEstoqueService: ConfigEstoqueService,
    private importacaoService: InventarioImportacaoService,
    private orquestradorService: InventarioOrquestradorService
  ) {}

  ngOnInit(): void {
    this.uploadForm = this.formBuilder.group({
      idLocalizacaoEstoque: [null],
      modoProcessamento: [0],
    });
    this._carregarLocais();
    this._carregarConfig();
  }

  private _carregarLocais(): void {
    this._isLoadingLocais = true;
    this.localizacaoService.getAll('', 1, 200).subscribe({
      next: (res) => {
        this._isLoadingLocais = false;
        if (!res.isError) this._localizacoes = res.listaLocalizacaoEstoque ?? [];
      },
      error: () => { this._isLoadingLocais = false; }
    });
  }

  private _carregarConfig(): void {
    this.configEstoqueService.get().subscribe({
      next: (res) => {
        if (!res.isError) {
          this._isEditarQuantidadeHabilitado = res.configuracaoEstoque?.isEditarQuantidadeImportacaoInventario ?? false;
        }
      }
    });
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this._isDragging = true;
  }

  public onDragLeave(): void {
    this._isDragging = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this._isDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this._setArquivo(file);
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this._setArquivo(file);
    input.value = '';
  }

  private _setArquivo(file: File): void {
    if (file.size > 2 * 1024 * 1024) {
      this.alertService.show('Arquivo muito grande', 'O arquivo deve ter no máximo 2MB.', AlertType.error);
      return;
    }
    this._arquivo = file;
  }

  public clickImportar(): void {
    if (!this._arquivo) {
      this.alertService.show('Arquivo obrigatório', 'Selecione um arquivo CSV para importação.', AlertType.error);
      return;
    }

    this._isValidating = true;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      const idLoc = this.uploadForm.get('idLocalizacaoEstoque')?.value;
      const nomeLoc = idLoc
        ? this._localizacoes.find(l => l.idLocalizacaoEstoque === idLoc)?.nome ?? ''
        : '';

      const request: ValidarPlanilhaRequest = {
        arquivoBase64: base64,
        idLocalizacaoEstoqueDefault: idLoc ?? undefined,
        nomeLocalizacaoDefault: nomeLoc || undefined,
        modoProcessamento: this.uploadForm.get('modoProcessamento')?.value ?? 0,
      };

      this.configEstoqueService.get().subscribe({
        next: (cfgRes) => {
          if (!cfgRes.isError) {
            this._isEditarQuantidadeHabilitado = cfgRes.configuracaoEstoque?.isEditarQuantidadeImportacaoInventario ?? false;
          }
          this.importacaoService.validarPlanilha(request).subscribe({
            next: (res) => {
              this._isValidating = false;
              if (res.isError) {
                this.alertService.show('Erro na validação', res.errorDescription ?? 'Erro desconhecido.', AlertType.error);
                return;
              }
              this._abrirPreview(res, idLoc ?? 0, nomeLoc);
            },
            error: () => {
              this._isValidating = false;
              this.alertService.show('Erro', 'Falha ao validar o arquivo.', AlertType.error);
            }
          });
        },
        error: () => {
          this._isValidating = false;
          this.alertService.show('Erro', 'Falha ao carregar a configuração.', AlertType.error);
        }
      });
    };
    reader.readAsDataURL(this._arquivo);
  }

  private _abrirPreview(validacao: any, idLoc: number, nomeLoc: string): void {
    const ref = this.dialog.open(ImportarPlanilhaPreviewDialogComponent, {
      width: '95vw',
      maxWidth: '1400px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        validacao,
        contexto: {
          idLocalizacaoEstoqueDefault: idLoc,
          nomeLocalizacaoDefault: nomeLoc,
          modoProcessamento: this.uploadForm.get('modoProcessamento')?.value ?? 0,
        },
        isEditarQuantidadeHabilitado: this._isEditarQuantidadeHabilitado,
      },
    });

    ref.afterClosed().subscribe((result: ImportarPreviewResult | undefined) => {
      if (!result) return;
      if (result.acao === 'escolherOutroArquivo') {
        this._arquivo = null;
        return;
      }
      if (result.acao === 'confirmado') {
        this._processarInventario(result.idInventario);
      }
    });
  }

  private _processarInventario(idInventario: number): void {
    this._isProcessing = true;
    this.orquestradorService.processar(idInventario).subscribe({
      next: (res) => {
        this._isProcessing = false;
        if (res.isError) {
          this.alertService.show('Erro ao processar', res.errorDescription ?? 'Erro desconhecido.', AlertType.error);
          return;
        }
        this.alertService.show('Importação concluída', 'Inventário criado e ajustes aplicados com sucesso.', AlertType.success);
        this.router.navigate(['/suprimentos/inventario']);
      },
      error: () => {
        this._isProcessing = false;
        this.alertService.show('Erro', 'Falha ao processar o inventário.', AlertType.error);
      }
    });
  }

  public clickDownloadModelo(): void {
    this._isDownloadingModelo = true;
    this.importacaoService.downloadPlanilhaModelo().subscribe({
      next: (blob) => {
        this._isDownloadingModelo = false;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'modelo-inventario-planilha.csv';
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this._isDownloadingModelo = false;
        this.alertService.show('Erro', 'Não foi possível baixar o arquivo modelo.', AlertType.error);
      }
    });
  }

  public clickVoltar(): void {
    this.router.navigate(['/suprimentos/inventario']);
  }

  public nomeArquivo(): string {
    return this._arquivo?.name ?? '';
  }

  public tamanhoArquivo(): string {
    if (!this._arquivo) return '';
    const kb = this._arquivo.size / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(0)} KB`;
  }
}
